import React, { useEffect, useMemo } from 'react';
import { Form as AntdForm } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField';
import FormSwitch from '../../../../antd/form/FormSwitch/FormSwitch';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer';
import FormButton from '../../../../antd/form/FormButton/FormButton';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import {
  useGetPhdSupervisorCandidatesQuery,
  useGetPhdSupervisorQuery,
  useSavePhdSupervisorMutation,
} from '../../../../../redux/services/phd.jsx';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';

const PhdSupervisorsEdit = ({ mode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = AntdForm.useForm();
  const isEdit = mode === 'edit';
  const type = AntdForm.useWatch('type', form) || 'fri';
  const pageTitle = isEdit ? t('Editácia školiteľa') : t('Pridať školiteľa');
  const { data: detail, isLoading: isLoadingDetail } = useGetPhdSupervisorQuery(
    id,
    { skip: !isEdit },
  );
  const { data: candidates, isLoading: isLoadingCandidates } =
    useGetPhdSupervisorCandidatesQuery(
      {
        type: isEdit ? detail?.type : type,
        include_person_id: isEdit ? detail?.person_id : undefined,
      },
      { skip: isEdit && !detail },
    );
  const [saveSupervisor, { isLoading: isSaving }] =
    useSavePhdSupervisorMutation();
  const { saveSuccess, saveFailed } = useToastMessages();
  const { markPristine, modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, isEdit ? id : 'create');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  useEffect(() => {
    if (isEdit && detail) {
      form.setFieldsValue(detail);
      markPristine();
      return;
    }

    if (!isEdit) {
      form.setFieldsValue({
        type: 'fri',
        active: true,
        person_id: undefined,
        external_university: undefined,
      });
      markPristine();
    }
  }, [detail, form, isEdit, markPristine]);

  const candidateOptions = useMemo(
    () =>
      (Array.isArray(candidates) ? candidates : candidates?.data || []).map(
        (candidate) => ({
          value: candidate.person_id,
          label: candidate.mail
            ? `${candidate.full_name} (${candidate.mail})`
            : candidate.full_name,
        }),
      ),
    [candidates],
  );

  const handleTypeChange = (value) => {
    form.setFieldsValue({
      type: value,
      person_id: undefined,
      external_university: undefined,
    });
  };

  const handleSubmit = async (values) => {
    try {
      await saveSupervisor({
        id: isEdit ? id : undefined,
        body: {
          person_id: values.person_id,
          type: values.type,
          active: values.active,
          external_university:
            values.type === 'external' ? values.external_university : null,
        },
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() => navigate(routes.cmsPhdSupervisors));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  if ((isEdit && isLoadingDetail) || isLoadingCandidates) {
    return <Spinner />;
  }

  return (
    <>
      {modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdSupervisors}
        backUrlTooltip={t('Školitelia')}
        permissionsBasic="phd-supervisors"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje evidencia školiteľov?')}
          description={t(
            'Vyberte osobu správneho typu a nastavte jej dostupnosť. Typ školiteľa po vytvorení nemožno zmeniť; neaktívny školiteľ zostáva v histórii, ale nebude v nových výberoch.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-user-tie"
          title={pageTitle}
          link={routes.cmsPhdSupervisors}
        >
          <Form
            form={form}
            initialValues={{ type: 'fri', active: true }}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
          >
            <FormSelect
              label={
                <span>
                  {t('Typ školiteľa')}
                  <PhdHelpHint
                    label={t('Čo znamená typ školiteľa?')}
                    title={t(
                      'FRI školiteľ je aktívny interný učiteľ. Externý školiteľ je evidovaný externý používateľ. Typ po vytvorení nemožno zmeniť.',
                    )}
                  />
                </span>
              }
              name="type"
              disabled={isEdit}
              options={[
                { value: 'fri', label: t('FRI') },
                { value: 'external', label: t('Externý') },
              ]}
              onChange={handleTypeChange}
              required
            />
            <FormSelect
              label={
                <span>
                  {t('Školiteľ')}
                  <PhdHelpHint
                    label={t('Ktoré osoby možno vybrať?')}
                    title={t(
                      'Zobrazené sú iba osoby vhodné pre zvolený typ, ktoré ešte nemajú záznam školiteľa.',
                    )}
                  />
                </span>
              }
              name="person_id"
              options={candidateOptions}
              required
            />
            {type === 'external' && (
              <FormTextField
                label={t('Univerzita')}
                name="external_university"
              />
            )}
            <FormSwitch
              label={
                <span>
                  {t('Aktívny')}
                  <PhdHelpHint
                    label={t('Čo znamená aktívny školiteľ?')}
                    title={t(
                      'Aktívny školiteľ je dostupný v nových výberoch tém a plánov. Deaktivácia zachová existujúce historické väzby.',
                    )}
                  />
                </span>
              }
              name="active"
            />
            <FormButtonsContainer>
              <FormButton submitOnClick disabled={isSaving}>
                {t('Uložiť')}
              </FormButton>
            </FormButtonsContainer>
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdSupervisorsEdit;
